import { immerable } from 'immer';

import { ProjectAssetEnum, ProjectStatusEnum } from 'api/enums';
import { IUserModel, UserModel } from './user.model';

/* -------------------------------------------------------------------------------------------------
 * IProjectAssetModel
 * -----------------------------------------------------------------------------------------------*/
export interface IProjectAssetModel {
    _id: string;
    name: ProjectAssetEnum;
    count: number;
}

/* -------------------------------------------------------------------------------------------------
 * IProjectImageModel
 * -----------------------------------------------------------------------------------------------*/
export interface IProjectImageModel {
    _id: string;
    url: string;
}

/* -------------------------------------------------------------------------------------------------
 * IProjectModel
 * -----------------------------------------------------------------------------------------------*/
export interface IProjectModel {
    _id: string;
    name: string;
    location: {
        name: string;
        latitude?: string;
        longitude?: string;
    };
    description: string;
    timeline: {
        startDate: string;
        endDate: string;
    };
    isExternalProject: boolean;
    coverImageUrl: string;
    images: IProjectImageModel[];
    iconUrl: string;
    status: ProjectStatusEnum;
    lastUpdated: Date;
    assets: IProjectAssetModel[];
    aiStats: {
        activityScore: number;
        ppeScore: number;
    };
    preferences: {
        mapZoom: number;
        // mapCenter: {
        //     latitude: string;
        //     longitude: string;
        // };
    };
    parentId: string;
    hasCameras: boolean;
    hierarchyLevel: number;
    constructionDays: number;
    createdAt: string;
    children: IProjectModel[];
    users: IUserModel[];
    stakeHolders: IProjectOrganizations[];
    cost: IProjectCostModel;
}

/* -------------------------------------------------------------------------------------------------
 * ProjectAssetModel
 * -----------------------------------------------------------------------------------------------*/
export class ProjectAssetModel {
    [immerable] = true;

    _id: string;
    name: ProjectAssetEnum;
    count: number;
    url: string;

    constructor(data: Partial<IProjectAssetModel>) {
        this._id = data._id ?? '';
        this.name = data.name ?? null;
        this.count = data.count ?? null;
    }

    static fromObject(data: Partial<IProjectAssetModel>): ProjectAssetModel {
        const model = new ProjectAssetModel(data);

        return model;
    }

    toObject(): IProjectAssetModel {
        const data: IProjectAssetModel = {
            _id: this._id,
            name: this.name,
            count: this.count,
        };

        return data;
    }
}

/* -------------------------------------------------------------------------------------------------
 * ProjectImageModel
 * -----------------------------------------------------------------------------------------------*/
export class ProjectImageModel {
    [immerable] = true;

    _id: string;
    url: string;

    constructor(data: Partial<IProjectImageModel>) {
        this._id = data._id ?? '';
        this.url = data.url ?? '';
    }

    static fromObject(data: Partial<IProjectImageModel>): ProjectImageModel {
        const model = new ProjectImageModel(data);

        return model;
    }

    static fromObjectList(data: Partial<IProjectImageModel>[]): ProjectImageModel[] {
        return data.map((row) => ProjectImageModel.fromObject(row));
    }

    static cloneFrom(data: Partial<IProjectImageModel>): ProjectImageModel {
        const model = new ProjectImageModel(data);

        return model;
    }

    toObject(): IProjectImageModel {
        const data: IProjectImageModel = {
            _id: this._id,
            url: this.url || null,
        };

        return data;
    }
}

/* -------------------------------------------------------------------------------------------------
 * ProjectModel
 * -----------------------------------------------------------------------------------------------*/
export class ProjectModel {
    [immerable] = true;

    _id: string;
    name: string;
    isExternal: boolean;
    location: {
        name: string;
        latitude: string;
        longitude: string;
    };
    description: string;
    stats: {
        timeline: {
            startDate: string;
            endDate: string;
        };
    };
    timeline: {
        startDate: string;
        endDate: string;
    };
    coverImageUrl: string;
    images: ProjectImageModel[];
    iconUrl: string;
    status: ProjectStatusEnum;
    lastUpdated: Date;
    assets: ProjectAssetModel[];
    aiStats: {
        activityScore: number;
        ppeScore: number;
    };
    preferences: {
        mapZoom: number;
        // mapCenter: {
        //     latitude: string;
        //     longitude: string;
        // };
    };
    parentId: string;
    hasCameras: boolean;
    hierarchyLevel: number;
    constructionDays: number;
    createdAt: string;

    // Relationships
    children?: ProjectModel[];
    users?: UserModel[];
    stakeHolders: IProjectOrganizations[];
    cost: IProjectCostModel;

    get hasTimeline() {
        return Boolean(this.timeline && this.timeline.startDate && this.timeline.endDate);
    }

    constructor(data: Partial<IProjectModel>) {
        this._id = data._id ?? '';
        this.name = data.name ?? '';
        this.isExternal = data.isExternalProject ?? null;
        this.location = {
            name: data.location?.name ?? '',
            latitude: data.location?.latitude ?? '',
            longitude: data.location?.longitude ?? '',
        };
        this.description = data.description ?? '';
        this.timeline = {
            startDate: data?.timeline?.startDate ?? '',
            endDate: data?.timeline?.endDate ?? '',
        };
        this.preferences = {
            mapZoom: data.preferences?.mapZoom ?? null,
            // mapCenter: {
            //     latitude: data.preferences?.mapCenter?.latitude ?? '',
            //     longitude: data.preferences?.mapCenter?.longitude ?? '',
            // },
        };

        this.coverImageUrl = data.coverImageUrl ?? '';
        this.images = data.images ? data.images.map((image) => ProjectImageModel.fromObject(image)) : [];
        this.iconUrl = data.iconUrl ?? '';
        this.status = data.status ?? null;
        this.lastUpdated = data.lastUpdated ?? null;
        this.assets = data.assets ? data.assets.map((asset) => ProjectAssetModel.fromObject(asset)) : [];
        this.aiStats = data.aiStats
            ? {
                  activityScore: data.aiStats.activityScore ?? 0,
                  ppeScore: data.aiStats.ppeScore ?? 0,
              }
            : null;
        this.parentId = data.parentId ?? '';
        this.hasCameras = data.hasCameras ?? null;
        this.hierarchyLevel = data.hierarchyLevel ?? null;
        this.constructionDays = data.constructionDays ?? null;
        this.createdAt = data.createdAt ?? null;
        this.cost = data.cost;
        this.stakeHolders = data.stakeHolders;

        if (data.children) {
            this.children = data.children.map((row) => ProjectModel.fromObject(row));
        }

        if (data.users) {
            this.users = data.users.map((row) => UserModel.fromObject(row));
        }
    }

    static fromObject(data: Partial<IProjectModel>): ProjectModel {
        const model = new ProjectModel(data);

        return model;
    }

    static fromObjectList(data: Partial<IProjectModel>[]): ProjectModel[] {
        return data.map((row) => ProjectModel.fromObject(row));
    }

    static cloneFrom(data: Partial<IProjectModel>): ProjectModel {
        const model = new ProjectModel(data);

        return model;
    }

    toObject(): IProjectModel {
        const data: Partial<IProjectModel> = {
            _id: this._id,
            name: this.name,
            location: {
                name: this.location.name || null,
                latitude: this.location.latitude || null,
                longitude: this.location.longitude || null,
            },
            isExternalProject: this.isExternal || null,
            description: this.description || null,
            timeline: this.hasTimeline
                ? {
                      startDate: this.timeline.startDate,
                      endDate: this.timeline.endDate,
                  }
                : null,
            coverImageUrl: this.coverImageUrl || null,
            images: this.images.map((image) => image.toObject()),
            iconUrl: this.iconUrl || null,
            status: this.status,
            lastUpdated: this.lastUpdated,
            assets: this.assets.map((asset) => asset.toObject()),
            aiStats: this.aiStats
                ? {
                      activityScore: this.aiStats.activityScore,
                      ppeScore: this.aiStats.ppeScore,
                  }
                : null,
            preferences: {
                mapZoom: this.preferences?.mapZoom,
                // mapCenter: {
                //     latitude: this.preferences?.mapCenter?.latitude || null,
                //     longitude: this.preferences?.mapCenter?.longitude || null,
                // },
            },
            parentId: this.parentId || null,
            hasCameras: this.hasCameras,
            hierarchyLevel: this.hierarchyLevel,
            constructionDays: this.constructionDays,
            createdAt: this.createdAt,
            cost: this.cost,
            stakeHolders: this.stakeHolders,
        };

        if (this.children) {
            data.children = this.children.map((child) => child.toObject());
        }

        if (this.users) {
            data.users = this.users.map((user) => user.toObject());
        }

        return data as IProjectModel;
    }
}

// Project COST
export interface IProjectCostModel {
    _id: string;
    amount: number;
    currency: string;
}

export interface IProjectOrganizations {
    name: string;
    type: string;
}

export interface IProjectStakeHolderModel {
    _id: string;
    organizations: IProjectOrganizations[];
}
